import {t} from 'i18next';
import {IconButton, Separator} from 'platform/components';
import {Box, Hide, HStack, Show, Space, useDevice, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {UseDocumentPreviewReturnType} from '../types/UseDocumentPreviewReturnType';
import {DocumentPreviewSlideshow} from './DocumentPreviewSlideshow';
import {DocumentPreviewThumbnails} from './DocumentPreviewThumbnails';

interface DocumentPreviewProps {
  documentPreview: UseDocumentPreviewReturnType;
}

const collapsibleSidebarVariants = cva(
  'relative flex flex-col items-center bg-[var(--color-palettes-neutral-30-100)] pb-2',
  {
    variants: {
      isCollapsed: {
        true: 'w-10 pt-0 pl-0',
        false: 'w-25 pt-4 pl-4',
      },
      isCompactDevice: {
        true: 'w-10 pt-0 pl-0',
        false: '',
      },
    },
  }
);

const rotatedTextVariants = cva(
  'absolute top-15 -rotate-90 whitespace-nowrap text-[length:var(--text-size-x-small)]'
);

export function DocumentPreview(props: DocumentPreviewProps) {
  const device = useDevice();

  const isCollapsed = props.documentPreview.state.isCollapsed;

  const handleCollapse = () => {
    props.documentPreview.controls.toggleCollapsed();
  };

  return (
    <HStack
      height="100%"
      width={isCollapsed ? 'auto' : '100%'}
      minWidth={isCollapsed ? 'auto' : '40%'}
    >
      <div
        className={cn(
          collapsibleSidebarVariants({
            isCollapsed,
            isCompactDevice: device === 'mobile' || device === 'tablet',
          })
        )}
      >
        <Show when={isCollapsed}>
          <HStack align="flex-start" justify="center" width="100%" height="100%">
            <div className={cn(rotatedTextVariants())}>
              {t('entity.documentPreview.labels.documentPreview')}
            </div>
          </HStack>
        </Show>

        <Show onMobile onTablet>
          <Box flexGrow={1} />
        </Show>

        <Hide when={isCollapsed} onMobile onTablet>
          <DocumentPreviewThumbnails documentPreview={props.documentPreview} />
        </Hide>

        <VStack shrink={0} width="100%" align="center">
          <Space fillAvailable />
          <Separator spacing={2} />
          <IconButton
            icon={isCollapsed ? 'action/collapse_open' : 'action/collapse_close'}
            onClick={handleCollapse}
          />
        </VStack>
      </div>

      <Hide when={isCollapsed}>
        <Box height="100%" width="100%" backgroundColor="palettes.neutral.30.100">
          <DocumentPreviewSlideshow documentPreview={props.documentPreview} />
        </Box>
      </Hide>
    </HStack>
  );
}
